import angular, { IPromise } from 'angular'

import { ApiRetailerPaymentType, ApiTender, ApiTransaction } from '@vend/types'
import { VdFeatureManager } from '@vend/vend.js'
import { getFromStorage } from '@vend/utilities'
import { PAYMENT_TYPES } from '@vend/business-components'
import {
  isGiftCardPaymentType,
  isBigCommerceGiftCertPaymentType,
} from '../functions/payments'
import { GiftCardsResource } from '../components/gift-cards/gift-cards-resource/gift-cards-resource.service'
import { giftCardFromBalancesApi } from '../components/gift-cards/utils'
import { $log, $q, $sce } from '../ng-compat/imports'
import { Payment } from '../models/payment'
import { channelsStore } from '../factories/database/channels-store'
import { EventEmitter } from '../util/event-emitter'
import {
  PaymentLinksAvailabilityResponse,
  PaymentLinksAvailabilityResult,
  checkPaymentLinksAvailability,
} from '../queries/sale_create'
import { client } from '../apollo'
import {
  ACTIVE_LSPAY_TRANSACTION,
  ActiveLSPayTransaction,
} from '../constants/payments'
import { paymentTypesStore } from '../factories/database/payment-types-store'
import { CashMovementsManager } from './cash-movements-manager'
import { RetailerSettingsManager } from './retailer-settings-manager'

// All other ApiTender fields are optional for receipt, except amount
export type ReceiptTender = Omit<Partial<ApiTender>, 'amount'> & {
  amount: number | string
}
// all other ApiTransaction fields are optional for receipt, except tenders with amount
export type ReceiptTransaction = Partial<Omit<ApiTransaction, 'tenders'>> & {
  tenders: ReceiptTender[]
}

export interface PrintReceiptEvent {
  isSignatureSlipReceipt?: boolean
  paymentType?: ApiRetailerPaymentType
  receiptHtmlExtra?: string
  transaction?: ReceiptTransaction
}

type Events = {
  printReceipt: [PrintReceiptEvent?]
}

export class PaymentManager extends EventEmitter<Events> {
  private _paymentIntegrationReceiptMessage: string | null = null

  private _acceptedPaymentIntegrationReceiptMessages: string[] = []

  constructor(
    private retailerSettingsManager: RetailerSettingsManager,
    private cashMovementsManager: CashMovementsManager,
    private readonly vdFeatureManager: VdFeatureManager,
    private BalancesResource: $TSFixMe,
    private giftCardsResource: GiftCardsResource
  ) {
    'ngInject'
    super()
  }

  /**
   * The receipt message returned from a payment integrator.
   *
   * @property paymentIntegrationReceiptMessage
   * @type {String}
   */
  public get paymentIntegrationReceiptMessage(): string | null {
    return this._paymentIntegrationReceiptMessage
  }

  public set paymentIntegrationReceiptMessage(value: string | null) {
    this._paymentIntegrationReceiptMessage = value
  }

  /**
   * The receipt message returned from a payment integrator.
   *
   * @property acceptedPaymentIntegrationReceiptMessages
   * @type {string[]}
   */
  public get acceptedPaymentIntegrationReceiptMessages(): string[] {
    return this._acceptedPaymentIntegrationReceiptMessages
  }

  public set acceptedPaymentIntegrationReceiptMessages(value: string[]) {
    this._acceptedPaymentIntegrationReceiptMessages = value
  }

  /**
   * Gets the payment portal url based on the payment type id, and sale data.
   *
   * @param  {String} option.basePaymentPortalUrl
   *         The base portal url
   *
   * @param  {Number|String} option.amount
   *         The value to set on the amount query param to two decimal places.
   *
   * @param  {Customer} option.customer
   *         The customer attached to the sale.
   *
   * @param  {String} params.registerId
   *         The id of the current register.
   *
   * @param  {String} params.referenceId
   *         The id of the current payment.
   *
   * @return {String} The payment portal url.
   */
  public getPaymentPortalUrl({
    basePaymentPortalUrl,
    amount,
    currency,
    retailerPaymentTypeId,
    customerId,
    registerId,
    referenceId,
  }: {
    basePaymentPortalUrl: string
    amount: number | string
    currency: string
    retailerPaymentTypeId: string
    customerId?: string
    registerId: string
    referenceId: string
  }) {
    const RETAILER_URL = this.retailerSettingsManager.getRetailerUrl()
    const origin = encodeURIComponent(RETAILER_URL)

    const customerParam = customerId ? `&customer_id=${customerId}` : ''
    const registerIdParam = registerId ? `&register_id=${registerId}` : ''
    const referenceIdParam = referenceId ? `&reference_id=${referenceId}` : ''
    const join = basePaymentPortalUrl.indexOf('?') === -1 ? '?' : '&'

    return $sce
      .getTrustedResourceUrl(
        $sce.trustAsResourceUrl(`
        ${basePaymentPortalUrl}${join}amount=${amount}${registerIdParam}&currency=${currency}&retailer_payment_type_id=${retailerPaymentTypeId}&origin=${origin}${customerParam}${referenceIdParam}
      `)
      )
      .trim()
  }

  /**
   * Determine if a provided payment is being used for cash management
   *
   * @param  {Object} payment
   *         The payment to test
   *
   * @return {Boolean} True if the payment type is being used for cash management, false otherwise
   */
  public isCashManagementPaymentType(payment: $TSFixMe): boolean {
    const cashManagementPaymentTypeId = this.cashMovementsManager.getCashManagementPaymentTypeId()
    return payment.payment_type_id === cashManagementPaymentTypeId
  }

  /**
   * Retrieves gift card information from the balances api when a gift card is used as a payment type
   *
   * @param  {Array<Payment>} payments
   *
   * @return {Promise} Array of gift card payments relevant to the sale
   */
  public getBalancesGiftCardPaymentsSummary(
    payments: Payment[]
  ): IPromise<unknown[]> {
    const giftCards: $TSFixMe[] = []
    const giftCardResolvers: $TSFixMe[] = []
    const giftCardPayments = payments.filter(payment =>
      isGiftCardPaymentType(payment)
    )

    giftCardPayments.forEach(payment => {
      giftCardResolvers.push(
        this.BalancesResource.getGiftCardByTransactionID(payment.sourceId)
          .then((balancesGiftCard: $TSFixMe) => {
            // Turns the balances api representation into the expected GiftCard representation.
            const giftCard = giftCardFromBalancesApi(balancesGiftCard)
            // As a type, this would be a conversion from GiftCard to GiftCardWithSourceId.
            ;(giftCard as $TSFixMe).sourceId = payment.sourceId
            giftCards.push(giftCard)
          })
          .catch((error: $TSFixMe) => {
            $log.warn(
              '[paymentManager] Failed to retrieve gift card by transaction id',
              error
            )
            return $q.reject(error)
          })
      )
    })
    return $q.all(giftCardResolvers).then(() => giftCards)
  }

  /**
   * Retrieves gift cert information from GraphQL when a gift cert is used as a payment type
   *
   * @param  {Array<Payment>} payments
   *
   * @return {Promise} Array of gift card payments relevant to the sale
   */
  public getBigCommerceGiftCertPaymentsSummary(
    payments: Payment[]
  ): IPromise<unknown[]> {
    const giftCards: $TSFixMe[] = []
    const giftCardResolvers: $TSFixMe[] = []
    const giftCardPayments = payments.filter(payment =>
      isBigCommerceGiftCertPaymentType(payment)
    )
    if (giftCardPayments.length === 0) {
      return $q.resolve([])
    }
    return channelsStore.getByChannelType('bigcommerce').then(channels => {
      if (channels.length !== 1) {
        return []
      }
      giftCardPayments.forEach(payment => {
        giftCardResolvers.push(
          this.giftCardsResource
            .getGiftCardByTransactionId(channels[0].id, payment.sourceId!)
            .then((giftCard: $TSFixMe) => {
              // As a type, this would be a conversion from GiftCard to GiftCardWithSourceId.
              giftCard.sourceId = payment.sourceId
              // We need to append a fake transaction for receipts, as it uses
              // the transactions for the amount and date.
              // See ReceiptPrintSelectedController#setGiftCardPayments
              giftCard.transactions.push({
                id: payment.sourceId,
                amount: payment.amount,
                type: 'REDEEMING',
                userId: '',
                createdAt: payment.paymentDate,
              })
              giftCards.push(giftCard)
            })
            .catch((error: $TSFixMe) => {
              $log.warn(
                '[paymentManager] Failed to retrieve gift card by transaction id',
                error
              )
              return $q.reject(error)
            })
        )
      })
      return $q.all(giftCardResolvers).then(() => giftCards)
    })
  }

  /**
   * Set paymentIntegrationReceiptMessage to be all accepted payment's receipt_html_extra message concatenated
   */
  public updateReceiptMessageOnSaleComplete(): void {
    this.paymentIntegrationReceiptMessage = this.acceptedPaymentIntegrationReceiptMessages.join(
      '<hr/>'
    )
  }

  /**
   * Set paymentIntegrationReceiptMessage to be print event's receipt_html_extra message
   */
  public updateReceiptMessageOnPrint(
    printReceiptMessage: string | null = null
  ): void {
    this.paymentIntegrationReceiptMessage = printReceiptMessage
  }

  /**
   * Set paymentIntegrationReceiptMessage to be decline event's receipt_html_extra message
   */
  public updateReceiptMessageOnDecline(declineReceiptMessage: string): void {
    this.paymentIntegrationReceiptMessage = declineReceiptMessage
  }

  /**
   * Gets the payment link availability for the current sale.
   *
   * @private
   * @method _retrievePaymentLinkAvailability
   *
   * @returns {Boolean}
   */
  public retrievePaymentLinkAvailability(
    activeOutletId: string
  ): IPromise<boolean> {
    return $q
      .resolve(
        client.query<PaymentLinksAvailabilityResult>({
          query: checkPaymentLinksAvailability,
          variables: { outletID: activeOutletId },
        })
      )
      .then(response =>
        this._handleCheckPaymentLinkAvailabilitySuccess(response)
      )
      .catch(() => this._handleCheckPaymentLinkAvailabilityFailure())
  }

  /**
   * Handle a successful check payment link availability
   * @private
   * @method _handleCheckPaymentLinkAvailabilitySuccess
   * @param response {PaymentLinksAvailabilityResponse}
   *        The data or error message returned by the server
   * @returns {boolean} A promise resolved once the payment link availability has been checked
   */
  private _handleCheckPaymentLinkAvailabilitySuccess(
    response: PaymentLinksAvailabilityResponse
  ): IPromise<boolean> {
    if (!response.data || (response.errors && response.errors.length > 0)) {
      return this._handleCheckPaymentLinkAvailabilityFailure()
    }
    return $q.resolve(response.data.lspayIsPaymentLinksAvailable)
  }

  /**
   * Handle a failed check payment link availability
   * @private
   * @method _handleCheckPaymentLinkAvailabilityFailure
   * @return {Promise} A rejected promise with the error message
   */
  private _handleCheckPaymentLinkAvailabilityFailure(): IPromise<never> {
    return $q.reject(`Failed to retrieve payment link availability for sale`)
  }

  /**
   * Checks and retrieves the active payment details for a given sale ID.
   * @param saleId - The ID of the sale.
   * @returns An object containing the active payment details:
   * - intentId: The ID of the payment intent, or undefined if not found.
   * - registerSalePaymentId: The ID of the register sale payment.
   * - amount: The amount of the payment.
   */
  public checkAndRetriveActivePayment(
    saleId: string
  ): {
    intentId?: string
    registerSalePaymentId: string | undefined
    amount?: string | number
  } {
    if (
      !this.vdFeatureManager.get('lspay_server_driven_override') &&
      !this.vdFeatureManager.get('lspay_pay_with_server_driven')
    ) {
      return {
        registerSalePaymentId: undefined,
      }
    }
    const { registerSaleId, paymentIntentId, registerSalePaymentId, amount } =
      getFromStorage<ActiveLSPayTransaction>(ACTIVE_LSPAY_TRANSACTION) || {}

    if (registerSaleId !== saleId) {
      return {
        registerSalePaymentId: undefined,
      }
    }
    return { intentId: paymentIntentId, registerSalePaymentId, amount }
  }

  /**
   * Checks if a retailer has an active lspay payment method.
   *
   * @return {Promise<Boolean>} True if the retailer has an active lspay payment method, false otherwise.
   */
  public async hasLspayPaymentType(): Promise<boolean> {
    // Wait for payment types, then find the first lspay payment type
    try {
      const paymentTypes = await paymentTypesStore.all()
      const lspayPaymentType = paymentTypes.find(
        paymentType =>
          !paymentType.deleted_at && paymentType.type_id === PAYMENT_TYPES.LSPAY
      )
      return !!lspayPaymentType
    } catch (error) {
      $log.warn('[PaymentManager] Failed to fetch payment types', error)
      return false
    }
  }
}

/**
 * Responsible for managing integrated payment gateway communication.
 *
 * @module paymentManager
 */
angular.module('webregister.managers').service('paymentManager', PaymentManager)
